import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import {
    X, Heart, MessageCircle, Bookmark,
    Send, Loader2, Sparkles, MoreVertical, Trash2, AlertCircle, Reply, Quote, Repeat
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { socialService } from '../services/socialService';
import AvatarRenderer from './AvatarRenderer';
import { useNavigate } from 'react-router-dom';
import { renderContentWithMentions } from '../utils/renderUtils';
import { extractPostStyle } from '../utils/postUtils';
import { useMention } from '../hooks/useMention';
import MentionDropdown from './MentionDropdown';

interface PostDetailModalProps {
    post: any;
    onClose: () => void;
    onUpdate?: () => void;
}

// ── Inline deletion confirmation modal ──────────────────────────────────────
const DeleteConfirmModal: React.FC<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
}> = ({ isOpen, message, onConfirm, onCancel, isLoading }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-surface p-8 rounded-[28px] w-full max-w-md animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
                <div className="w-12 h-12 rounded-2xl mb-6 flex items-center justify-center bg-tint-rose text-bad">
                    <AlertCircle size={24} />
                </div>
                <h3 className="text-lg font-semibold dark:text-ink mb-2">{t('social.confirmDeletion')}</h3>
                <p className="text-muted text-sm mb-8">{message}</p>
                <div className="grid grid-cols-2 gap-3">
                    <button
                        onClick={(e) => { e.stopPropagation(); onCancel(); }}
                        disabled={isLoading}
                        className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full"
                    > {t('social.cancel')}</button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onConfirm(); }}
                        disabled={isLoading}
                        className="text-[13px] font-medium h-12 bg-bad text-white rounded-full disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={14} />}
                        {isLoading ? t('social.deleting') : t('social.delete')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Single comment item (recursive for replies) ──────────────────────────────
interface CommentItemProps {
    comm: any;
    isReply?: boolean;
    currentUserId: string;
    getReplies: (parentId: string) => any[];
    onDelete: (id: string) => void;
    onReply: (comm: any) => void;
    onReact: (commentId: string, emoji: string) => void;
    onNavigate: (username: string) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
    comm, isReply = false, currentUserId, getReplies, onDelete, onReply, onReact, onNavigate
}) => {
    const { t, i18n } = useTranslation();
    const [showReactions, setShowReactions] = useState(false);
    const commUsername    = comm.profiles?.username    || comm.username    || '';
    const commDisplayName = comm.profiles?.display_name || comm.display_name || commUsername;
    const commAvatarUrl   = comm.profiles?.avatar_url   || comm.avatar_url   || '';
    const commAvatarCfg   = (() => {
        const raw = comm.profiles?.avatar_config || comm.avatar_config;
        if (!raw) return null;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        // An empty object {} (from D1 COALESCE default) is not a real avatar config
        if (!parsed || !parsed.body) return null;
        return parsed;
    })();
    const isOwnComment = String(comm.user_id) === String(currentUserId);
    const replies = getReplies(String(comm.id));

    return (
        <div className={isReply ? 'ml-10 pl-4 border-l-2 border-black/[0.06] dark:border-white/[0.06] space-y-3' : 'space-y-3'}>
            <div className="flex gap-3">
                {/* Avatar */}
                <button
                    className="w-8 h-8 rounded-xl overflow-hidden bg-tint-stone/60 flex-shrink-0 hover:opacity-80 transition-opacity"
                    onClick={() => onNavigate(commUsername)}
                >
                    {commAvatarCfg
                        ? <AvatarRenderer config={commAvatarCfg} seed={commUsername} />
                        : <img
                            src={commAvatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${commDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                            className="w-full h-full object-cover"
                            alt={commDisplayName}
                            onError={e => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${commDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                          />
                    }
                </button>

                {/* Bubble + actions */}
                <div className="flex-1 min-w-0">
                    <div className="bg-tint-stone/40 p-3 rounded-2xl rounded-tl-none">
                        <div className="flex items-center gap-2 mb-1">
                            <button
                                className="font-semibold text-[10px] text-ink hover:text-primary transition-colors text-left block"
                                onClick={() => onNavigate(commUsername)}
                            >
                                {commDisplayName}
                            </button>
                            <span className="text-[9px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                                {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(comm.created_at))}
                            </span>
                        </div>
                        <p className="text-xs text-muted leading-relaxed font-medium">
                            {renderContentWithMentions(comm.content, onNavigate)}
                        </p>
                    </div>
                    {/* Sub-actions */}
                    <div className="flex items-center gap-4 mt-1 px-1">
                        <button
                            onClick={() => onReply(comm)}
                            className="text-[9px] font-normal text-muted hover:text-primary transition-colors uppercase font-mono tracking-[0.14em] flex items-center gap-1"
                        >
                            <Reply size={10} /> {t('social.reply')}</button>
                        {isOwnComment && (
                            <button
                                onClick={() => onDelete(String(comm.id))}
                                className="text-[9px] font-normal text-muted hover:text-bad transition-colors uppercase font-mono tracking-[0.14em] flex items-center gap-1"
                            >
                                <Trash2 size={10} /> {t('social.delete')}</button>
                        )}
                    </div>

                    {/* Reactions show */}
                    <div className="flex flex-wrap gap-2 mt-2 px-1">
                        {comm.reactions?.map((r: any) => (
                            <button 
                                key={r.emoji}
                                onClick={() => onReact(String(comm.id), r.emoji)}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold transition-all ${r.reacted ? 'bg-primary/20 text-primary border border-primary/80' : 'bg-tint-stone/40 text-muted hover:bg-tint-stone/60 '}`}
                            >
                                <span>{r.emoji}</span>
                                <span>{r.count}</span>
                            </button>
                        ))}
                        <button 
                            onClick={() => setShowReactions(!showReactions)}
                            className="w-6 h-6 rounded-full bg-tint-stone/40 text-muted hover:text-primary transition-all flex items-center justify-center text-[14px]"
                        >
                            +
                        </button>
                    </div>

                    {/* Reaction Selector */}
                    {showReactions && (
                        <div className="flex gap-2 p-2 px-3 bg-surface rounded-2xl border border-black/[0.06] mt-2 w-fit animate-in zoom-in-95 origin-top-left z-10">
                            {['❤️', '🔥', '👏', '😂', '😮', '😢'].map(emoji => (
                                <button 
                                    key={emoji} 
                                    onClick={() => { onReact(String(comm.id), emoji); setShowReactions(false); }} 
                                    className="text-lg hover:scale-125 transition-transform"
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Nested replies */}
            {replies.length > 0 && (
                <div className="space-y-3">
                    {replies.map(reply => (
                        <CommentItem
                            key={reply.id}
                            comm={reply}
                            isReply
                            currentUserId={currentUserId}
                            getReplies={getReplies}
                            onDelete={onDelete}
                            onReply={onReply}
                            onReact={onReact}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Main Modal ───────────────────────────────────────────────────────────────
const PostDetailModal: React.FC<PostDetailModalProps> = ({ post, onClose, onUpdate }) => {
  const { t } = useTranslation();

    const { user } = useAuth();
    const { accentColor } = useTheme();
    const { friends } = useData();
    const navigate = useNavigate();

    const [comments,       setComments]       = useState<any[]>([]);
    const [loadingComments, setLoadingComments] = useState(true);
    const [hasMoreComments, setHasMoreComments] = useState(false);
    const [isLoadingMore, setIsLoadingMore]   = useState(false);
    
    const {
        text: newComment,
        setText: setNewComment,
        handleKeyDown,
        handleSelectMention,
        handleCursorChange,
        mentionResults,
        selectedIndex,
        showDropdown
    } = useMention('', friends);

    const [isPosting,      setIsPosting]      = useState(false);
    const [showCommentsMobile, setShowCommentsMobile] = useState(false);

    const [liked,      setLiked]      = useState(false);
    const [saved,      setSaved]      = useState(false);
    const [likesCount, setLikesCount] = useState(post.likes_count || 0);

    // Menus & deletion
    const [showPostMenu,       setShowPostMenu]       = useState(false);
    const [deletePostConfirm,  setDeletePostConfirm]  = useState(false);
    const [deletingPost,       setDeletingPost]       = useState(false);
    const [deleteCommentId,    setDeleteCommentId]    = useState<string | null>(null);
    const [deletingComment,    setDeletingComment]    = useState(false);

    // Reply
    const [replyTo, setReplyTo] = useState<any | null>(null);

    // The logged-in user owns this post?
    const isOwnPost = !!user && (
        String(post.user_id) === String(user.id) ||
        (post.profiles?.username && user.username && post.profiles.username === user.username)
    );

    // Author info (from the nested profiles object returned by db.ts JOIN)
    const authorUsername    = post.profiles?.username    || '';
    const authorDisplayName = post.profiles?.display_name || authorUsername;
    const authorAvatarUrl   = post.profiles?.avatar_url   || '';
    const authorAvatarCfg   = (() => {
        const raw = post.profiles?.avatar_config;
        if (!raw) return null;
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    })();

    // ── Fetch interactions + comments ───────────────────────────────────────
    useEffect(() => { fetchEngagement(); }, [post.id]);

    // Close post menu when clicking anywhere outside
    useEffect(() => {
        const handler = () => setShowPostMenu(false);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const fetchEngagement = async () => {
        // 1. SWR: Try to load from cache first
        const cached = socialService.getCachedComments(post.id);
        if (cached) {
            setComments(cached);
            setLoadingComments(false);
        } else {
            setLoadingComments(true);
        }

        try {
            // 2. Background update
            const [interactionsResult, commentsResult] = await Promise.allSettled([
                socialService.getPostInteractions(post.id),
                socialService.getPostComments(post.id, user?.id)
            ]);

            if (interactionsResult.status === 'fulfilled') {
                const data = interactionsResult.value;
                const raw  = Array.isArray(data) ? data : (data?.data || []);
                if (user) {
                    const uid = String(user.id);
                    const pid = String(post.id);
                    setLiked(raw.some((i: any) => String(i.user_id) === uid && String(i.post_id) === pid && i.type === 'like'));
                    setSaved(raw.some((i: any) => String(i.user_id) === uid && String(i.post_id) === pid && i.type === 'save'));
                }
            }

            if (commentsResult.status === 'fulfilled') {
                const data = commentsResult.value;
                const newComments = Array.isArray(data) ? data : (data?.data || []);
                setComments(newComments);
                setHasMoreComments(newComments.length === 10);
            }
        } catch (err) {
            console.error('[PostDetailModal] fetchEngagement:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const loadMoreComments = async () => {
        setIsLoadingMore(true);
        const currentCount = comments.length;
        try {
            const more = await socialService.getPostComments(post.id, user?.id, 10, currentCount);
            if (more && more.length > 0) {
                setComments(prev => {
                    const existingIds = new Set(prev.map(c => c.id));
                    const uniqueMore = more.filter(c => !existingIds.has(c.id));
                    return [...prev, ...uniqueMore];
                });
                setHasMoreComments(more.length === 10);
            } else {
                setHasMoreComments(false);
            }
        } catch(e) {
            console.error('[PostDetailModal] loadMoreComments:', e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    // ── Interaction handlers ────────────────────────────────────────────────
    const handleLike = async () => {
        if (!user) return;
        const prev = liked; const prevC = likesCount;
        setLiked(!prev); setLikesCount(c => prev ? Math.max(0, c - 1) : c + 1);
        try {
            await socialService.toggleInteraction(String(user.id), String(post.id), 'like');
            onUpdate?.();
        } catch { setLiked(prev); setLikesCount(prevC); }
    };

    const handleSave = async () => {
        if (!user) return;
        const prev = saved; setSaved(!prev);
        try {
            await socialService.toggleInteraction(String(user.id), String(post.id), 'save');
            onUpdate?.();
        } catch { setSaved(prev); }
    };

    const handleRepost = async () => {
        if (!user) return;
        try {
            await socialService.toggleInteraction(String(user.id), String(post.id), 'repost');
            onUpdate?.();
        } catch (err) {
            console.error('[PostDetailModal] handleRepost:', err);
        }
    };

    // ── Comment handlers ────────────────────────────────────────────────────
    const handlePostComment = async () => {
        if (!user || !newComment.trim()) return;
        setIsPosting(true);
        try {
            await socialService.addPostComment(
                String(user.id),
                String(post.id),
                newComment.trim(),
                replyTo ? String(replyTo.id) : null
            );
            setNewComment('');
            setReplyTo(null);
            fetchEngagement();
            onUpdate?.();
        } catch (err) {
            console.error('[PostDetailModal] addComment:', err);
        } finally {
            setIsPosting(false);
        }
    };

    const handleDeleteComment = async () => {
        if (!user || !deleteCommentId) return;
        setDeletingComment(true);
        try {
            await socialService.deletePostComment(deleteCommentId, String(user.id));
            setDeleteCommentId(null);
            fetchEngagement();
            onUpdate?.();
        } catch (err) {
            console.error('[PostDetailModal] deleteComment:', err);
        } finally {
            setDeletingComment(false);
        }
    };

    const handleCommentReact = async (commentId: string, emoji: string) => {
        if (!user) return;
        try {
            await socialService.togglePostCommentReaction(String(user.id), commentId, emoji);
            fetchEngagement();
        } catch (err) {
            console.error('[PostDetailModal] toggleReaction:', err);
        }
    };

    // ── Post deletion ───────────────────────────────────────────────────────
    const handleDeletePost = async () => {
        if (!user) return;
        setDeletingPost(true);
        try {
            // 1. Cascade-delete all comments first
            await socialService.deleteAllPostComments(String(user.id), String(post.id));
            // 2. Delete the post itself
            await socialService.deletePost(String(post.id), String(user.id));
            onUpdate?.();
            onClose();
        } catch (err) {
            console.error('[PostDetailModal] deletePost:', err);
        } finally {
            setDeletingPost(false);
            setDeletePostConfirm(false);
        }
    };

    // ── Navigation ──────────────────────────────────────────────────────────
    const goToProfile = (username: string) => {
        if (!username) return;
        onClose();
        navigate(`/profile/${username}`);
    };

    // ── Thread helpers ──────────────────────────────────────────────────────
    const rootComments = comments.filter(c => !c.parent_id);
    const getReplies   = (parentId: string) => comments.filter(c => String(c.parent_id) === parentId);

    // Check if we are in community-feed to remove animations
    const isFeedRoute = window.location.pathname.startsWith('/community-feed');
    const rootAnimationClasses = isFeedRoute ? "" : "animate-in fade-in duration-300";
    const modalAnimationClasses = isFeedRoute ? "" : "animate-in zoom-in-95 duration-300";

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div
            className={`fixed inset-0 z-[200] flex items-center justify-center p-0 md:p-4 bg-surface md:bg-black/30 backdrop-blur-xl ${rootAnimationClasses}`}
            onClick={onClose}
        >
            <div
                className={`bg-surface w-full h-full md:h-auto md:max-w-4xl md:rounded-[3rem] overflow-hidden flex flex-col md:flex-row md:border md:border-black/[0.06] dark:md:border-white/[0.06] relative ${modalAnimationClasses}`}
                style={{ maxHeight: '100vh', height: '100dvh', ...(window.innerWidth >= 768 ? { maxHeight: '90vh', height: 'auto' } : {}) }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Close Button (Mobile) ── */}
                <button
                    onClick={onClose}
                    className="absolute top-4 left-4 p-2 bg-black/40 backdrop-blur-md rounded-full text-white z-50 md:hidden"
                >
                    <X size={20} />
                </button>

                {/* ── LEFT SIDE: Snapshot/Photo (Full Screen on Mobile) ── */}
                <div
                    className="relative flex-shrink-0 bg-ink overflow-hidden flex items-stretch w-full h-full md:w-[min(45%,calc(90vh*9/16))]"
                >
                    {/* Inner wrapper keeps 9:16 and lets content fill completely */}
                    <div className="w-full h-full md:aspect-[9/16]">
                        {(() => {
                            const parsed = extractPostStyle(post.content);
                            const firstImg = post.media_url || (parsed.style?.imgs ? Object.values(parsed.style.imgs)[0] : null);
                            
                            if (firstImg) {
                                return (
                                    <img
                                        src={firstImg as string}
                                        className="w-full h-full object-contain md:object-contain"
                                        alt="Post"
                                        style={{ background: '#000' }}
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                );
                            } else {
                                return (
                                    <div 
                                        className="w-full h-full p-8 flex flex-col justify-center items-center text-center gap-4 relative overflow-hidden"
                                        style={{ 
                                            backgroundColor: parsed.style?.bg || '#1a0533',
                                            fontFamily: parsed.style?.fId || 'inherit',
                                            color: parsed.style?.color || '#ffffff'
                                        }}
                                    >
                                        <Quote className="w-12 h-12 opacity-10 absolute top-10 left-10 -rotate-12" />
                                        <p className="text-xl md:text-2xl font-medium leading-tight italic drop-shadow-lg">
                                            "{parsed.text.slice(0, 150)}{parsed.text.length > 150 ? '...' : ''}"
                                        </p>
                                        <div className="w-12 h-px bg-white/20 my-4" />
                                        <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] opacity-60">{post.book_title}</p>
                                    </div>
                                );
                            }
                        })()}
                    </div>

                    {/* ── Mobile Overlay: Action Buttons + Author Info ── */}
                    <div className="md:hidden absolute inset-0 pointer-events-none flex flex-col justify-end p-6 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                        {/* Action Buttons (Right Side) */}
                        <div className="absolute right-4 bottom-32 flex flex-col gap-6 items-center pointer-events-auto">
                            <button
                                onClick={handleLike}
                                className={`flex flex-col items-center gap-1 transition-all ${liked ? 'text-bad' : 'text-white'}`}
                            >
                                <Heart size={28} className={liked ? 'fill-bad' : ''} />
                                <span className="text-[10px] font-semibold">{likesCount}</span>
                            </button>
                            <button
                                onClick={() => setShowCommentsMobile(true)}
                                className="flex flex-col items-center gap-1 text-white"
                            >
                                <MessageCircle size={28} />
                                <span className="text-[10px] font-semibold">{comments.length}</span>
                            </button>
                            <button
                                onClick={handleRepost}
                                className="flex flex-col items-center gap-1 text-white"
                            >
                                <Repeat size={28} />
                            </button>
                            <button
                                onClick={handleSave}
                                className={`transition-all ${saved ? 'text-primary' : 'text-white'}`}
                            >
                                <Bookmark size={28} className={saved ? 'fill-primary' : ''} />
                            </button>
                        </div>

                        {/* Author Info (Bottom) */}
                        <div className="flex flex-col gap-2 max-w-[80%] pointer-events-auto">
                            <div className="flex items-center gap-3" onClick={() => goToProfile(authorUsername)}>
                                <div className="w-9 h-9 rounded-full overflow-hidden border border-white/40">
                                    {authorAvatarCfg
                                        ? <AvatarRenderer config={authorAvatarCfg} seed={authorUsername} />
                                        : <img
                                            src={authorAvatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${authorDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                            className="w-full h-full object-cover"
                                            alt={authorDisplayName}
                                            onError={e => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${authorDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                          />
                                    }
                                </div>
                                <span className="text-sm font-bold text-white drop-shadow-md">@{authorUsername}</span>
                            </div>
                            {post.book_title && (
                                <p className="text-[10px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full self-start border border-primary/20">
                                    {post.book_title}
                                </p>
                            )}
                            <p className="text-xs text-white/90 font-medium leading-relaxed line-clamp-3 drop-shadow-md">
                                {renderContentWithMentions(post.user_opinion || post.content || '', goToProfile)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT SIDE: Engagement panel (Desktop sidebar / Mobile bottom sheet) ── */}
                <div className={` ${showCommentsMobile ? 'fixed inset-0 z-[100] md:relative' : 'hidden md:flex'}
                    animate-in slide-in-from-bottom-10 duration-300
                `}>
                    {/* Header: Mobile close comments button */}
                    <div className="md:hidden h-1.5 w-12 bg-tint-stone rounded-full mx-auto mt-3 mb-1 flex-shrink-0" onClick={() => setShowCommentsMobile(false)} />


                    {/* Header: author (clickable) + 3-dot menu + X */}
                    <div className="p-5 border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between flex-shrink-0">
                        {/* Mobile back button for comments */}
                        <button onClick={() => setShowCommentsMobile(false)} className="p-2 md:hidden text-muted">
                            <X size={20} />
                        </button>

                        {/* Author */}
                        <button
                            className="flex items-center gap-3 hover:opacity-75 transition-opacity"
                            onClick={() => goToProfile(authorUsername)}
                        >
                            <div className="w-10 h-10 rounded-2xl overflow-hidden border border-primary/20 bg-tint-stone/60 flex-shrink-0">
                                {authorAvatarCfg
                                    ? <AvatarRenderer config={authorAvatarCfg} seed={authorUsername} />
                                    : <img
                                        src={authorAvatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${authorDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                        className="w-full h-full object-cover"
                                        alt={authorDisplayName}
                                        onError={e => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${authorDisplayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                      />
                                }
                            </div>
                            <div className="text-left">
                                <h3 className="font-bold text-ink text-sm flex items-center gap-2">
                                    {authorDisplayName}
                                    {post.profiles?.plan_type === 'pro' && <Sparkles size={12} className="text-primary fill-primary" />}
                                </h3>
                                <p className="text-[10px] text-muted font-mono">@{authorUsername}</p>
                            </div>
                        </button>

                        {/* Right: 3-dot (post owner) + X */}
                        <div className="flex items-center gap-1">
                            {isOwnPost && (
                                <div className="relative">
                                    <button
                                        onClick={e => { e.stopPropagation(); setShowPostMenu(p => !p); }}
                                        className="p-2 hover:bg-tint-stone/60 rounded-full text-muted hover:text-ink transition-colors"
                                        title="{t('social.moreOptions')}"
                                    >
                                        <MoreVertical size={20} />
                                    </button>
                                    {showPostMenu && (
                                        <div className="absolute right-0 top-10 bg-surface rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] border border-black/[0.06] py-2 min-w-[160px] z-[110] animate-in zoom-in-95">
                                            <button
                                                onClick={e => { e.stopPropagation(); setShowPostMenu(false); setDeletePostConfirm(true); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 text-bad hover:bg-tint-rose dark:hover:bg-tint-rose/10 transition-colors text-sm font-semibold"
                                            >
                                                <Trash2 size={14} /> {t('social.deletePost')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <button
                                onClick={onClose}
                                className="hidden md:block p-2 hover:bg-tint-stone/60 rounded-full text-muted transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Stats bar: likes, comment count, save (Desktop Only) */}
                    <div className="hidden md:flex items-center px-5 py-3 border-b border-black/[0.06] dark:border-white/[0.06] gap-6 flex-shrink-0">
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 transition-all ${liked ? 'text-bad' : 'text-muted hover:text-bad'}`}
                        >
                            <Heart size={20} className={liked ? 'fill-bad' : ''} />
                            <span className="text-[10px] font-semibold">{likesCount}</span>
                        </button>
                        <div className="flex items-center gap-2 text-muted">
                            <MessageCircle size={20} />
                            <span className="text-[10px] font-semibold">{comments.length}</span>
                        </div>
                        <button
                            onClick={handleRepost}
                            className="flex items-center gap-2 text-muted hover:text-primary transition-all"
                        >
                            <Repeat size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`ml-auto transition-all ${saved ? 'text-primary' : 'text-muted hover:text-primary'}`}
                        >
                            <Bookmark size={20} className={saved ? 'fill-primary' : ''} />
                        </button>
                    </div>

                    {/* Post description / opinion / book title */}
                    {/* Post description / opinion / book title (Desktop Only) */}
                    {(post.book_title || post.user_opinion || post.content) && (
                        <div className="hidden md:block px-5 py-3 border-b border-black/[0.06] dark:border-white/[0.06] flex-shrink-0 space-y-1">
                            {post.book_title && (
                                <p className="text-[10px] font-normal text-primary uppercase font-mono tracking-[0.14em]">{post.book_title}</p>
                            )}
                            {(post.user_opinion || post.content) && (
                                <p className="text-xs text-muted font-medium leading-relaxed line-clamp-3">
                                    {renderContentWithMentions(post.user_opinion || post.content, goToProfile)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Comments list */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
                        {loadingComments ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="animate-spin text-primary" size={24} />
                            </div>
                        ) : rootComments.length === 0 ? (
                            <div className="text-center py-12 px-8 space-y-3 opacity-40">
                                <MessageCircle className="mx-auto w-8 h-8" />
                                <p className="text-xs font-normal uppercase font-mono tracking-[0.14em]">{t('social.noCommentsYet2')}</p>
                            </div>
                        ) : (
                            rootComments.map(comm => (
                                <CommentItem
                                    key={comm.id}
                                    comm={comm}
                                    currentUserId={String(user?.id ?? '')}
                                    getReplies={getReplies}
                                    onDelete={id => setDeleteCommentId(id)}
                                    onReply={c => setReplyTo(c)}
                                    onReact={handleCommentReact}
                                    onNavigate={goToProfile}
                                />
                            ))
                        )}

                        {hasMoreComments && rootComments.length > 0 && !loadingComments && (
                            <div className="flex justify-center pt-2 pb-4">
                                <button onClick={loadMoreComments} disabled={isLoadingMore} className="text-xs font-normal text-primary hover:text-primary/80 transition-colors uppercase font-mono tracking-[0.14em] flex items-center gap-2">
                                    {isLoadingMore ? <Loader2 className="animate-spin" size={14} /> : null}
                                    {t('social.loadMoreComments')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Reply indicator bar */}
                    {replyTo && (
                        <div className="px-5 py-2 bg-primary/5 border-t border-primary/20 flex items-center justify-between flex-shrink-0">
                            <p className="text-[9px] font-normal text-primary flex items-center gap-1 uppercase font-mono tracking-[0.14em]">
                                <Reply size={10} />
                                {t('social.replyingTo', { username: replyTo.profiles?.username || replyTo.username })}
                            </p>
                            <button onClick={() => setReplyTo(null)} className="text-muted hover:text-muted">
                                <X size={14} />
                            </button>
                        </div>
                    )}

                    {/* Comment input */}
                    <div className="p-5 border-t border-black/[0.06] dark:border-white/[0.06] bg-surface flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    placeholder={
                                        replyTo
                                            ? t('social.replyPlaceholder', { username: replyTo.profiles?.username || replyTo.username })
                                            : t('social.addComment')
                                    }
                                    value={newComment}
                                    onChange={(e) => {
                                        setNewComment(e.target.value);
                                        handleCursorChange(e.target.selectionStart || 0);
                                    }}
                                    onKeyDown={(e) => {
                                        if (mentionResults.length > 0) {
                                            handleKeyDown(e);
                                        } else if (e.key === 'Enter') {
                                            handlePostComment();
                                        }
                                    }}
                                    onKeyUp={(e) => handleCursorChange((e.target as HTMLInputElement).selectionStart || 0)}
                                    onMouseUp={(e) => handleCursorChange((e.target as HTMLInputElement).selectionStart || 0)}
                                    className="w-full bg-tint-stone/60 px-5 py-3.5 rounded-2xl outline-none text-sm dark:text-ink border-none"
                                />
                                <MentionDropdown
                                    results={mentionResults}
                                    selectedIndex={selectedIndex}
                                    onSelect={handleSelectMention}
                                />
                            </div>
                            <button
                                onClick={handlePostComment}
                                disabled={isPosting || !newComment.trim()}
                                className="bg-primary text-slate-900 p-3.5 rounded-2xl disabled:opacity-50 hover:scale-105 transition-all"
                            >
                                {isPosting ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete POST confirmation */}
            <DeleteConfirmModal
                isOpen={deletePostConfirm}
                message={t('social.confirmDeletePostFull')}
                onConfirm={handleDeletePost}
                onCancel={() => setDeletePostConfirm(false)}
                isLoading={deletingPost}
            />

            {/* Delete COMMENT confirmation */}
            <DeleteConfirmModal
                isOpen={!!deleteCommentId}
                message={t('social.confirmDeleteComment')}
                onConfirm={handleDeleteComment}
                onCancel={() => setDeleteCommentId(null)}
                isLoading={deletingComment}
            />
        </div>
    );
};

export default PostDetailModal;
